import { Typography } from '@mui/material';
import {
  BackupJobType,
  Provider,
  ResourceType,
  type BackupJob,
} from '@repo/api-gw-sdk';

import {
  sourceAccountId,
  backupRegion,
  backupJobInitiatorId,
  jobId,
  jobTemporalLink,
  jobResourceName,
  resourceId,
  sourceRegion,
  vault,
  resourceType,
} from '@/components/queryBuilder/properties';
import { AccountAsVaultTag } from '@/components/vaults/vaultTag';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

export const backupJobItemFields = [
  jobId,
  jobTemporalLink,
  jobResourceName,
  resourceType,
  resourceId,
  backupJobInitiatorId,
] as FilterProperty<BackupJob>[];

export const backupJobSummaryFromFields = [
  sourceAccountId,
  sourceRegion,
] as FilterProperty<BackupJob>[];

export const backupJobSummaryToFields = [
  vault,
  backupRegion,
] as FilterProperty<BackupJob>[];

// FSx/EFS backups are stored in AWS Backup Vault in the source account
const nativeAwsBackupStorage: FilterProperty<BackupJob> = {
  name: 'nativeAwsBackupStorage',
  displayName: 'Storage',
  type: PropertyType.String,
  group: 'backup-to',
  field: {
    Component: ({ entity }) => (
      <AccountAsVaultTag
        cloudProvider={Provider.Aws}
        region={entity.resourceDetails?.region || ''}
      />
    ),
  },
};

const nativeAwsBackupAccountId: FilterProperty<BackupJob> = {
  name: 'nativeAwsBackupAccountId',
  displayName: 'Account',
  type: PropertyType.String,
  group: 'backup-to',
  field: {
    Component: ({ entity }) => (
      <Typography>{entity.resourceDetails?.providerAccountId}</Typography>
    ),
  },
};

const nativeAwsBackupRegion: FilterProperty<BackupJob> = {
  name: 'nativeAwsBackupRegion',
  displayName: 'Backup region',
  type: PropertyType.String,
  group: 'backup-to',
  field: {
    Component: ({ entity }) => (
      <Typography>{entity.resourceDetails?.region}</Typography>
    ),
  },
};

// Kept for backward compatibility
export const fsxBackupJobSummaryToFields = [
  nativeAwsBackupStorage,
  nativeAwsBackupAccountId,
  nativeAwsBackupRegion,
] as FilterProperty<BackupJob>[];

/**
 * Checks if a backup job is a native AWS Backup job that stores data in the source account
 * (FSx/EFS backups are stored in AWS Backup Vault in the source account)
 */
export const isNativeAwsBackupJob = (job: BackupJob): boolean => {
  // Check by backup type first
  if (
    job.backupType === BackupJobType.AwsFsxBackup ||
    job.backupType === BackupJobType.AwsEfsBackup
  ) {
    return true;
  }

  // Fallback: check by resource type when backup type is unspecified
  if (
    job.resourceDetails?.resourceType === ResourceType.AwsFsx ||
    job.resourceDetails?.resourceType === ResourceType.AwsEfs
  ) {
    return true;
  }

  return false;
};
